import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Tooltip,
} from "@mui/material";
import {useUserDeleteMutation, useUserListQuery} from "../../app/api/api";
import {ColumnDef, FilterDef, FilterOperator, FilterType, ListRequestIdModelTable} from "../../components/ExTable";
import DeleteIcon from "@mui/icons-material/Delete";
import CreateIcon from "@mui/icons-material/AddCircleOutline";
import {useTranslation} from "react-i18next";
import {formatTimestamp} from "../../utils/datetime";
import {Link} from "react-router-dom";
import {useSnackbar} from "notistack";
import {useModal} from "../../hooks/useModal";
import {PERMISSION_LEVEL_LABEL_KEYS, PermissionLevel, User, UserField, UserStatus} from "../../app/api/model/user";
import Page from "../../components/Layout/Page";
import {ordinaryDetailPageLinkTo} from "../../utils/ordinaryDetailPageLink";
import {RoutePath} from "../../types";

export default function UserList() {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const [deleteUser] = useUserDeleteMutation();
    const deleteModal = useModal(false);

    const columnDefs: ColumnDef<User>[] = [
        {
            fieldName: UserField.ID,
            align: "right",
            label: t("generic_id"),
            width: 120,
            sortable: true,
            generateLink: ordinaryDetailPageLinkTo(RoutePath.USERS),
            sticky: true,
        },
        {
            fieldName: UserField.FIRST_NAME,
            align: "left",
            label: t("user_firstName"),
            width: 200,
            sortable: true,
            generateLink: ordinaryDetailPageLinkTo(RoutePath.USERS),
        },
        {
            fieldName: UserField.LAST_NAME,
            align: "left",
            label: t("user_lastName"),
            width: 200,
            sortable: true,
            generateLink: ordinaryDetailPageLinkTo(RoutePath.USERS),
        },
        {
            fieldName: UserField.EMAIL,
            align: "left",
            label: t("user_email"),
            width: 300,
            sortable: true,
        },
        {
            fieldName: UserField.STATUS,
            align: "left",
            label: t("user_status"),
            width: 150,
            sortable: true,
        },
        {
            fieldName: UserField.PERMISSION_LEVEL,
            align: "right",
            label: t("user_levelShort"),
            width: 120,
            sortable: true,
            formatCellText: ({cellValue}) => {
                return t(PERMISSION_LEVEL_LABEL_KEYS[cellValue as PermissionLevel]);
            },
        },
        {
            fieldName: UserField.CREATED_AT,
            align: "left",
            label: t("generic_createdAt"),
            width: 180,
            sortable: true,
            formatCellText: ({cellValue}) => {
                return formatTimestamp(cellValue);
            },
        },
        {
            fieldName: UserField.UPDATED_AT,
            align: "left",
            label: t("generic_updatedAt"),
            width: 180,
            sortable: true,
            formatCellText: ({cellValue}) => {
                return formatTimestamp(cellValue);
            },
        },
    ];

    const filterDefs: FilterDef<User>[] = [
        {
            fieldName: "anywhere",
            label: t("generic_filterAnywhere"),
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: UserField.ID,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
        },
        {
            fieldName: UserField.FIRST_NAME,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: UserField.LAST_NAME,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: UserField.EMAIL,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: UserField.STATUS,
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            type: FilterType.ENUM,
            enumValues: [
                {
                    value: UserStatus.ACTIVE,
                    labelKey: "status_active",
                },
                {
                    value: UserStatus.INACTIVE,
                    labelKey: "status_inactive",
                },
            ],
        },
        {
            fieldName: UserField.PERMISSION_LEVEL,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.NOT,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.ENUM,
            enumValues: Object.entries(PERMISSION_LEVEL_LABEL_KEYS).map(([value, labelKey]) => ({
                value,
                labelKey,
            })),
        },
        {
            fieldName: UserField.CREATED_AT,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.DATE,
        },
        {
            fieldName: UserField.UPDATED_AT,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.DATE,
        },
    ];

    const handleDelete = (id: number) => {
        deleteModal.onOpen({id});
    };

    const commitDelete = async () => {
        await deleteUser(deleteModal.payload?.id as number).unwrap();
        enqueueSnackbar(t("generic_deleteSuccessfully"), {variant: "success"});
        deleteModal.toggleIsOpen();
    };

    return (
        <Page>
            <ListRequestIdModelTable
                dataTest="usersList"
                queryHook={useUserListQuery}
                columnDefs={columnDefs}
                filterDefs={filterDefs}
                title={t("generic_users")}
                renderActions={(keys) => (
                    <>
                        {keys.length === 0 && (
                            <Tooltip title={t("action_createUser")}>
                                <IconButton component={Link} to={"new"}>
                                    <CreateIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        {keys.length === 1 && (
                            <Tooltip title={t("action_deleteUser")}>
                                <IconButton onClick={() => handleDelete(keys[0].id)}>
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </>
                )}
            />
            <Dialog
                open={deleteModal.isOpen}
                onClose={deleteModal.toggleIsOpen}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{t("generic_confirmDelete")}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t("confirm_deleteRecordWithId", {id: deleteModal.payload?.id})}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={deleteModal.toggleIsOpen}>{t("action_cancel")}</Button>
                    <Button onClick={commitDelete} autoFocus color="error">
                        {t("action_deleteUser")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Page>
    );
}
